import { useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { jsonStore } from "@/manager/cache/localdb";
import { getSubscriptionSettings } from "@/services/push-info-user-subscription-setting-list";

export enum AgenUserSubscriptionType {
  video = "video",
  podcast = "podcast",
  moments = "moments",
  post = "post",
  article = "article",
}

export type AgenUserSubscriptionInfo = {
  subscribed: boolean;
  pushFrequency: number;
  pushQuantity: number;
  pushTime: string;
};

type ChangePayload = {
  type: AgenUserSubscriptionType;
  info: AgenUserSubscriptionInfo;
};

type SubscriptionEvent = CustomEvent<ChangePayload>;

class AgentUserSubscriptionEvents {
  constructor(private eventName: string) {}
  listen(listener: (e: SubscriptionEvent) => void): () => void {
    window.addEventListener(this.eventName, listener as EventListener);
    return () => {
      window.removeEventListener(this.eventName, listener as EventListener);
    };
  }
  dispatch(detail: ChangePayload): void {
    const event = new CustomEvent<ChangePayload>(this.eventName, { detail });
    window.dispatchEvent(event);
  }
}

class AgentUserSubscriptionLocal {
  constructor(private storageKey: string) {}
  async get(): Promise<AgenUserSubscriptionInfo | null> {
    return await jsonStore.get<AgenUserSubscriptionInfo>(this.storageKey);
  }
  async set(info: AgenUserSubscriptionInfo): Promise<void> {
    await jsonStore.set(this.storageKey, info);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AgentUserSubscriptionRemote {
  constructor(private cache: AgentUserSubscriptionLocal, private agentId: string, private type: AgenUserSubscriptionType) {}
  async getCached() { return await this.cache.get(); }
  async fetch(): Promise<AgenUserSubscriptionInfo> {
    const data = await getSubscriptionSettings({ agentId: this.agentId, type: this.type });
    const info: AgenUserSubscriptionInfo = {
      subscribed: data.subscribeEnabled,
      pushFrequency: data.pushFrequency,
      pushQuantity: data.pushCount,
      pushTime: data.pushTime,
    };
    await this.cache.set(info);
    return info;
  }
}

export class AgenUserSubscriptionManager {
  private readonly STORAGE_PREFIX = "MIUTA_AGENT_USER_SUBSCRIPTION";
  private agentId?: string;
  private currentType: AgenUserSubscriptionType;
  private info: AgenUserSubscriptionInfo | null = null;
  private cache: AgentUserSubscriptionLocal;
  private api: AgentUserSubscriptionRemote;
  private events: AgentUserSubscriptionEvents;

  constructor(agentId?: string, initialType: AgenUserSubscriptionType = AgenUserSubscriptionType.video) {
    this.agentId = agentId;
    this.currentType = initialType;
    const key = `${this.STORAGE_PREFIX}:${this.agentId ?? "default"}:${this.currentType}`;
    this.cache = new AgentUserSubscriptionLocal(key);
    this.api = new AgentUserSubscriptionRemote(this.cache, this.agentId || "", this.currentType);
    this.events = new AgentUserSubscriptionEvents(key);
  }

  public async init(): Promise<void> {
    try {
      const cached = await this.api.getCached();
      if (cached) {
        this.info = cached;
        this.dispatch();
        this.fetch();
        return;
      }
    } catch {}
    await this.fetch();
  }

  public getType(): AgenUserSubscriptionType {
    return this.currentType;
  }

  public getInfo(): AgenUserSubscriptionInfo {
    return this.info || { subscribed: false, pushFrequency: 0, pushQuantity: 0, pushTime: "" };
  }

  public listen(listener: (e: SubscriptionEvent) => void): () => void {
    return this.events.listen(listener);
  }

  private async fetch(): Promise<void> {
    const info = await this.api.fetch();
    this.info = info;
    this.dispatch();
  }

  public reset(): void {
    this.info = null;
    this.clear();
  }

  public clear(): void {
    this.cache.clear();
  }

  private dispatch(): void {
    this.events.dispatch({ type: this.currentType, info: this.getInfo() });
  }
}

export const agentUserSubscriptionHooks = {
  useGetAgentUserSubscriptionInfo(agentId: string | undefined, type: AgenUserSubscriptionType) {
    const managerRef = useRef<AgenUserSubscriptionManager | null>(null);
    const [info, setInfo] = useState<AgenUserSubscriptionInfo | null>(null);
    const { showToastByCode } = useToast();

    useEffect(() => {
      managerRef.current = new AgenUserSubscriptionManager(agentId, type);
      const unsubscribe = managerRef.current.listen((e) => setInfo(e.detail.info));
      managerRef.current.init().catch((error) => showToastByCode(error));
      return unsubscribe;
    }, [agentId, type]);

    return info;
  },
};

export default AgenUserSubscriptionManager;